import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MediationDecision } from "@expertloop/shared";
import { ReplyDraftChoiceDialog } from "./ReplyDraftChoiceDialog";
import { DecisionPreview } from "./DecisionPreview";
import { useReplyDraft } from "./useReplyDraft";

interface MediationDecisionPanelProps {
	orderId: string;
	currency: string;
	/** Une vente encaissée se rembourse ; une vente seulement réservée se libère ou s'encaisse. */
	isSettled: boolean;
	/** Ce qui reste acquis à l'expert, donc le maximum remboursable (vente encaissée). */
	remainingSettled: number;
	/** Plafond autorisé (vente seulement réservée). */
	authorizedAmount: number;
	/** Montant que l'expert encaisserait si le support clôt sans remboursement (vente seulement réservée). */
	captureAmount: number;
	/** Un rapport prêt existe dans la langue de consultation : le brouillon de réponse peut être demandé. */
	reportReady: boolean;
	/** Langue du rapport, donc du brouillon. */
	reportLocale: string;
	message: string;
	onMessageChange: (value: string) => void;
	refundInput: string;
	onRefundInputChange: (value: string) => void;
	plannedRefund: number;
	isRefundValid: boolean;
	resolving: boolean;
	error: string;
	onChoose: (decision: MediationDecision) => void;
}

/**
 * Panneau d'arbitrage (MEDIATIONUX-4) : message, montant, simulation de chaque issue et
 * choix, sans masquer le dossier. Rien n'est exécuté ici : le choix ouvre la fenêtre de
 * confirmation, qui présente l'opération exacte. Les libellés décrivent le geste, pas un camp.
 */
export function MediationDecisionPanel(props: MediationDecisionPanelProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();
	const { currency, isSettled, remainingSettled, plannedRefund, isRefundValid } = props;
	const money = (cents: number) => formatCents(cents, currency);
	const draft = useReplyDraft(props.orderId, props.reportLocale, props.message, props.onMessageChange);

	// Sur une vente encaissée, un champ vide veut dire « tout rembourser » : clôturer sans remboursement
	// suppose de saisir 0, pour que le geste choisi et le montant ne se contredisent jamais.
	const typedAmount = props.refundInput.trim() === "" ? null : Number(props.refundInput.replace(",", "."));
	const closeAllowed = !isSettled || typedAmount === 0;

	const refundSimulation = isSettled
		? isRefundValid
			? [
					{ label: t("adminChatDetail.decision.simulation.clientReceives"), value: money(plannedRefund) },
					{ label: t("adminChatDetail.decision.simulation.expertKeeps"), value: money(remainingSettled - plannedRefund) },
				]
			: null
		: [
				{ label: t("adminChatDetail.decision.simulation.clientReleased"), value: money(props.authorizedAmount) },
				{ label: t("adminChatDetail.decision.simulation.expertReceives"), value: money(0) },
			];
	const closeSimulation = isSettled
		? [
				{ label: t("adminChatDetail.decision.simulation.clientReceives"), value: money(0) },
				{ label: t("adminChatDetail.decision.simulation.expertKeeps"), value: money(remainingSettled) },
			]
		: [
				{ label: t("adminChatDetail.decision.simulation.clientReceives"), value: money(0) },
				{ label: t("adminChatDetail.decision.simulation.expertReceives"), value: money(props.captureAmount) },
			];

	return (
		<section className="card mediation-decision" id="mediation-decision">
			<div className="card-header">
				<h2>{t("adminChatDetail.decision.title")}</h2>
				<span className="mediation-chip">{t("adminChatDetail.decision.humanValidation")}</span>
			</div>
			<div className="card-body">
				<p className="translation-indicative">{t("adminChatDetail.decision.intro")}</p>

				<div className="resolution-message-input">
					<div className="mediation-draft-bar">
						<label htmlFor="mediation-message">{t("adminChatDetail.supportDecisionMessage")}</label>
						<button
							type="button"
							className="btn btn-sm btn-outline"
							disabled={!props.reportReady || draft.generating}
							title={props.reportReady ? undefined : t("adminChatDetail.draftReply.needsReport")}
							onClick={draft.request}
						>
							<i className="fas fa-wand-magic-sparkles" aria-hidden="true"></i>{" "}
							{draft.generating ? t("adminChatDetail.draftReply.generating") : t("adminChatDetail.draftReply.button")}
						</button>
					</div>
					{draft.error && <div className="resolution-error" role="alert">{draft.error}</div>}
					<textarea
						id="mediation-message"
						value={props.message}
						onChange={(event) => props.onMessageChange(event.target.value)}
						placeholder={t("adminChatDetail.decisionPlaceholder")}
						rows={5}
					/>
					<DecisionPreview orderId={props.orderId} message={props.message} />
					{props.error && <div className="resolution-error">{props.error}</div>}
				</div>

				{isSettled && (
					<div className="resolution-refund-input">
						<label htmlFor="mediation-refund">{t("adminChatDetail.refundAmountLabel")}</label>
						<input
							id="mediation-refund"
							type="number"
							min="0"
							step="0.01"
							max={(remainingSettled / 100).toFixed(2)}
							value={props.refundInput}
							placeholder={(remainingSettled / 100).toFixed(2)}
							onChange={(event) => props.onRefundInputChange(event.target.value)}
						/>
						<span className="resolution-refund-hint">{t("adminChatDetail.refundAmountHint", { remaining: money(remainingSettled) })}</span>
					</div>
				)}

				<div className="mediation-outcomes" aria-live="polite">
					<section className="mediation-outcome mediation-outcome--refund" aria-label={t("adminChatDetail.decision.simulation.ifRefund")}>
						<h3>{t("adminChatDetail.decision.simulation.ifRefund")}</h3>
						{refundSimulation ? (
							<dl>
								{refundSimulation.map((row) => (
									<div key={row.label}>
										<dt>{row.label}</dt>
										<dd>{row.value}</dd>
									</div>
								))}
							</dl>
						) : (
							<p>{t("adminChatDetail.decision.simulation.invalidAmount")}</p>
						)}
						<button
							type="button"
							className="btn btn-success"
							onClick={() => props.onChoose("favor_client")}
							disabled={props.resolving || !props.message.trim() || (isSettled && !isRefundValid)}
						>
							{t("adminChatDetail.decision.grantRefund")}
							<span className="btn-subtitle">{isSettled ? t("adminChatDetail.decision.grantRefundSettled") : t("adminChatDetail.decision.grantRefundUnsettled")}</span>
						</button>
					</section>

					<section className="mediation-outcome mediation-outcome--close" aria-label={t("adminChatDetail.decision.simulation.ifClose")}>
						<h3>{t("adminChatDetail.decision.simulation.ifClose")}</h3>
						<dl>
							{closeSimulation.map((row) => (
								<div key={row.label}>
									<dt>{row.label}</dt>
									<dd>{row.value}</dd>
								</div>
							))}
						</dl>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => props.onChoose("favor_expert")}
							disabled={props.resolving || !props.message.trim() || !closeAllowed}
						>
							{t("adminChatDetail.decision.close")}
							<span className="btn-subtitle">{isSettled ? t("adminChatDetail.decision.closeSettled") : t("adminChatDetail.decision.closeUnsettled")}</span>
						</button>
						{!closeAllowed && <p className="translation-indicative">{t("adminChatDetail.decision.simulation.closeNeedsZero")}</p>}
					</section>
					<p className="translation-indicative">{t("adminChatDetail.decision.simulation.feesNote")}</p>
				</div>
			</div>
			{draft.isChoosing && <ReplyDraftChoiceDialog onChoose={draft.choose} onCancel={draft.keepCurrent} />}
		</section>
	);
}
